// @ts-nocheck
import MarkdownBoldTextarea from "./MarkdownBoldTextarea"
import { DraftMarkdownSnippet } from "./DraftMarkdownSnippet"
import { wrapMarkdownBold } from "../utils/markdownBoldWrap.js"

import React, { useRef } from "react"
import { canAddMoreItems, canRemoveItems, getFieldLimits } from "../constants/validation"

// Simple List Editor Component for arrays
const SimpleListEditor = ({ value, onChange, fieldName = null, maxItems = null, minItems = null }) => {
  // Parse the value, handling both array and string formats
  const parseItems = (val) => {
    if (!val) return [""]
    if (typeof val === "string") {
      // Split by newlines and remove any bullet points/dashes at the start of each line
      const lines = val.split("\n").map((line) => line.replace(/^[-•]\s*/, "").trim())
      return lines.length > 0 ? lines : [""]
    }
    return Array.isArray(val) ? val : [""]
  }

  const items = parseItems(value)
  
  // Get validation limits from constants or props
  const limits = fieldName ? getFieldLimits(fieldName) : null
  const effectiveMaxItems = maxItems || limits?.max || null
  const effectiveMinItems = minItems || limits?.min || null
  
  // Calculate validation state
  const canAdd = !effectiveMaxItems || items.length < effectiveMaxItems
  const canRemove = !effectiveMinItems || items.length > effectiveMinItems

  const inputRefs = useRef({})

  const handleItemChange = (index, newValue) => {
    const newItems = [...items]
    newItems[index] = newValue
    // Don't add bullet points during editing - just store the raw text
    onChange(newItems)
  }

  const addItem = () => {
    if (!canAdd) return
    const newItems = [...items, ""]
    onChange(newItems)
  }

  const removeItem = (index) => {
    if (!canRemove) return
    const newItems = items.filter((_, i) => i !== index)
    // Ensure at least one empty item remains if no minimum specified
    if (newItems.length === 0 && !effectiveMinItems) newItems.push("")
    onChange(newItems)
  }

  const wrapBoldAt = (index) => {
    const el = inputRefs.current[index]
    if (!el) return
    const line = items[index] ?? ""
    const start = el.selectionStart
    const end = el.selectionEnd
    const { nextText, selectionStart, selectionEnd } = wrapMarkdownBold(line, start, end)
    handleItemChange(index, nextText)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(selectionStart, selectionEnd)
    })
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div key={index} className="space-y-1 rounded-md border border-gray-100 bg-white p-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={(el) => {
                inputRefs.current[index] = el
              }}
              type="text"
              value={item}
              onChange={(e) => handleItemChange(index, e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "b") {
                  e.preventDefault()
                  wrapBoldAt(index)
                }
              }}
              className="min-w-0 flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={`Item ${index + 1}`}
            />
            <button
              type="button"
              onClick={() => wrapBoldAt(index)}
              className="shrink-0 px-2 py-1 text-xs font-medium bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Bold
            </button>
            <button
              type="button"
              onClick={() => removeItem(index)}
              disabled={!canRemove}
              className={`shrink-0 px-2 py-1 rounded text-xs ${
                canRemove ? "text-red-600 hover:bg-red-50" : "text-gray-400 cursor-not-allowed"
              }`}
            >
              ✕
            </button>
          </div>
          <div className="rounded border border-gray-100 bg-gray-50 px-2 py-1.5 text-xs">
            <span className="font-medium text-gray-500">Preview </span>
            <DraftMarkdownSnippet className="inline text-gray-700">{item || " "}</DraftMarkdownSnippet>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        disabled={!canAdd}
        className={`w-full px-3 py-2 text-sm border rounded-md ${
          canAdd
            ? "text-blue-600 border-blue-300 hover:bg-blue-50"
            : "text-gray-400 border-gray-300 cursor-not-allowed bg-gray-50"
        }`}
      >
        + Add Item {effectiveMaxItems && `(${items.length}/${effectiveMaxItems})`}
      </button>
      {effectiveMaxItems && !canAdd && (
        <p className="text-red-500 text-xs">Maximum {effectiveMaxItems} items allowed</p>
      )}
      {effectiveMinItems && !canRemove && (
        <p className="text-red-500 text-xs">Minimum {effectiveMinItems} items required</p>
      )}
    </div>
  )
}

// Challenges Editor Component for bullet_points + explanation structure
const ChallengesEditor = ({ value, onChange }) => {
  // Parse initial value once and manage local state
  const parseInitialValue = (val) => {
    if (!val) return { bullet_points: [""], explanation: "" }
    
    if (typeof val === "string") {
      // Try to parse string that might contain bullet points and explanation
      const lines = val.split("\n")
      const bulletPoints = []
      let explanation = ""
      let explanationStarted = false
      
      for (const line of lines) {
        const trimmed = line.trim()
        
        // If we find an empty line after bullet points, start collecting explanation
        if (!trimmed && bulletPoints.length > 0 && !explanationStarted) {
          explanationStarted = true
          continue
        }
        
        // If we're collecting explanation or find non-bullet content after bullets
        if (explanationStarted || (bulletPoints.length > 0 && !trimmed.startsWith("-") && !trimmed.startsWith("•"))) {
          explanationStarted = true
          explanation += (explanation ? "\n" : "") + trimmed
        } else if (trimmed.startsWith("•") || trimmed.startsWith("-")) {
          bulletPoints.push(trimmed.replace(/^[•-]\s*/, ""))
        } else if (trimmed && bulletPoints.length === 0) {
          bulletPoints.push(trimmed)
        }
      }
      
      return {
        bullet_points: bulletPoints.length > 0 ? bulletPoints : [""],
        explanation: explanation || ""
      }
    }
    
    if (typeof val === "object" && val !== null) {
      return {
        bullet_points: Array.isArray(val.bullet_points) ? val.bullet_points : [""],
        explanation: val.explanation || ""
      }
    }
    
    return { bullet_points: [""], explanation: "" }
  }

  // Use local state to prevent feedback loops
  const [localState, setLocalState] = React.useState(() => parseInitialValue(value))

  // Update local state when external value changes (but not from our own changes)
  React.useEffect(() => {
    const newParsed = parseInitialValue(value)
    const currentMarkdown = formatChallengesAsMarkdown(localState.bullet_points, localState.explanation)
    
    // Only update if the external value is different from what we would generate
    if (value !== currentMarkdown) {
      setLocalState(newParsed)
    }
  }, [value])

  const formatChallengesAsMarkdown = (bulletPoints, explanationText) => {
    const validBulletPoints = bulletPoints.filter(item => item && item.trim())
    const bulletPointsText = validBulletPoints.map(item => `- ${item}`).join('\n')
    
    if (explanationText && explanationText.trim()) {
      return `${bulletPointsText}\n\n${explanationText}`
    }
    
    return bulletPointsText
  }

  const updateAndNotify = (newBulletPoints, newExplanation) => {
    const newState = { bullet_points: newBulletPoints, explanation: newExplanation }
    setLocalState(newState)
    
    const markdownString = formatChallengesAsMarkdown(newBulletPoints, newExplanation)
    onChange(markdownString)
  }

  const handleBulletPointsChange = (newBulletPoints) => {
    updateAndNotify(newBulletPoints, localState.explanation)
  }

  const handleExplanationChange = (newExplanation) => {
    updateAndNotify(localState.bullet_points, newExplanation)
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Challenge Items
        </label>
        <SimpleListEditor value={localState.bullet_points} onChange={handleBulletPointsChange} />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Explanation
        </label>
        <textarea
          value={localState.explanation}
          onChange={(e) => handleExplanationChange(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
          rows={3}
          placeholder="Enter explanation for the challenges..."
        />
      </div>
    </div>
  )
}

// Business Outcomes Editor Component for key-value pairs structure
const BusinessOutcomesEditor = ({ value, onChange }) => {
  // Parse initial value once and manage local state
  const parseInitialValue = (val) => {
    if (!val) return [{ key: "", value: "" }]
    
    if (typeof val === "string") {
      // Parse markdown format like "### Title1\nDescription1\n\n### Title2\nDescription2"
      // Handle cases where content might start with ### or have ### embedded
      let content = val.trim()
      
      // Split on ### but preserve the ### for processing
      const sections = content.split(/(?=###\s+)/).filter(section => section.trim())
      const outcomes = []
      
      for (const section of sections) {
        const lines = section.split('\n')
        if (lines.length > 0) {
          // Extract title from first line (remove ### prefix)
          const firstLine = lines[0].trim()
          if (firstLine.startsWith('###')) {
            const key = firstLine.replace(/^###\s*/, '').trim()
            const value = lines.slice(1).join('\n').trim()
            if (key) {
              outcomes.push({ key, value })
            }
          }
        }
      }
      
      return outcomes.length > 0 ? outcomes : [{ key: "", value: "" }]
    }
    
    if (typeof val === "object" && val !== null && !Array.isArray(val)) {
      return Object.entries(val).map(([key, value]) => ({
        key: key,
        value: String(value)
      }))
    }
    
    if (Array.isArray(val)) {
      return val.map(item => ({
        key: typeof item === "object" ? item.key || "" : String(item),
        value: typeof item === "object" ? item.value || "" : ""
      }))
    }
    
    return [{ key: "", value: "" }]
  }

  // Use local state to prevent feedback loops
  const [localOutcomes, setLocalOutcomes] = React.useState(() => parseInitialValue(value))

  // Update local state when external value changes (but not from our own changes)
  React.useEffect(() => {
    const newParsed = parseInitialValue(value)
    const currentMarkdown = formatBusinessOutcomesAsMarkdown(localOutcomes)
    
    // Only update if the external value is different from what we would generate
    if (value !== currentMarkdown) {
      setLocalOutcomes(newParsed)
    }
  }, [value])

  const formatBusinessOutcomesAsMarkdown = (outcomesList) => {
    const validOutcomes = outcomesList.filter(outcome => outcome.key && outcome.key.trim())
    
    return validOutcomes
      .map(outcome => `### ${outcome.key}\n${outcome.value || ""}`)
      .join('\n\n')
  }

  const updateAndNotify = (newOutcomes) => {
    setLocalOutcomes(newOutcomes)
    const markdownString = formatBusinessOutcomesAsMarkdown(newOutcomes)
    onChange(markdownString)
  }

  // Auto-resize textareas on initial load
  React.useEffect(() => {
    const textareas = document.querySelectorAll('textarea')
    textareas.forEach(textarea => {
      if (textarea.value) {
        textarea.style.height = 'auto'
        textarea.style.height = Math.max(48, textarea.scrollHeight) + 'px'
      }
    })
  }, [localOutcomes])

  const handleOutcomeChange = (index, field, newValue) => {
    const newOutcomes = [...localOutcomes]
    newOutcomes[index] = { ...newOutcomes[index], [field]: newValue }
    updateAndNotify(newOutcomes)
  }

  const addOutcome = () => {
    const newOutcomes = [...localOutcomes, { key: "", value: "" }]
    updateAndNotify(newOutcomes)
  }

  const removeOutcome = (index) => {
    const newOutcomes = localOutcomes.filter((_, i) => i !== index)
    // Ensure at least one empty outcome remains
    if (newOutcomes.length === 0) newOutcomes.push({ key: "", value: "" })
    updateAndNotify(newOutcomes)
  }

  return (
    <div className="space-y-3">
      {localOutcomes.map((outcome, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={outcome.key}
                onChange={(e) => handleOutcomeChange(index, 'key', e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={`Outcome ${index + 1} (e.g., "Federal market expansion")`}
              />
              <button
                type="button"
                onClick={() => removeOutcome(index)}
                className="px-2 py-1 text-red-600 hover:bg-red-50 rounded flex-shrink-0"
              >
                ✕
              </button>
            </div>
            <textarea
              value={outcome.value}
              onChange={(e) => {
                handleOutcomeChange(index, 'value', e.target.value)
                // Auto-resize textarea
                e.target.style.height = 'auto'
                e.target.style.height = Math.max(48, e.target.scrollHeight) + 'px'
              }}
              onInput={(e) => {
                // Auto-resize on initial render and paste
                e.target.style.height = 'auto'
                e.target.style.height = Math.max(48, e.target.scrollHeight) + 'px'
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none overflow-hidden"
              rows={2}
              placeholder="Describe the outcome in detail..."
              style={{ minHeight: '48px' }}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addOutcome}
        className="w-full px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
      >
        + Add Business Outcome
      </button>
    </div>
  )
}

// Client Testimonial Editor Component - Updated for new Pydantic model structure
const ClientTestimonialEditor = ({ value, onChange }) => {
  // Parse testimonial content from different formats
  const parseTestimonial = (val) => {
    if (!val) return { quote: "", author_name: "", author_title: "", company: "" }
    
    // Handle already structured object (new format)
    if (typeof val === "object" && val !== null && !Array.isArray(val)) {
      return {
        quote: val.quote || "",
        author_name: val.author_name || "",
        author_title: val.author_title || "",
        company: val.company || ""
      }
    }
    
    if (typeof val === "string") {
      // Handle legacy quoted format: "Quote text" — Person Name, Title, Company
      const quotedPattern = /^"([^"]*)"(?:\s*[—–-]\s*(.*?)(?:,\s*(.*?)(?:,\s*(.*))?)?)?$/
      const match = val.match(quotedPattern)
      
      if (match) {
        return {
          quote: match[1] || "",
          author_name: match[2] || "",
          author_title: match[3] || "",
          company: match[4] || ""
        }
      }
      
      // Handle plain text without quotes - treat as just the quote
      return { quote: val.trim(), author_name: "", author_title: "", company: "" }
    }
    
    return { quote: "", author_name: "", author_title: "", company: "" }
  }

  const [testimonial, setTestimonial] = React.useState(() => parseTestimonial(value))

  // Update local state when external value changes
  React.useEffect(() => {
    const newParsed = parseTestimonial(value)
    // Only update if significantly different to prevent loops
    if (JSON.stringify(newParsed) !== JSON.stringify(testimonial)) {
      setTestimonial(newParsed)
    }
  }, [value])

  const updateTestimonial = (field, newValue) => {
    const updated = { ...testimonial, [field]: newValue }
    setTestimonial(updated)
    // Pass the structured object directly to match new backend expectation
    onChange(updated)
  }

  const formatPreview = ({ quote, author_name, author_title, company }) => {
    if (!quote.trim()) return ""
    
    let attribution = ""
    if (author_name.trim()) {
      attribution = author_name.trim()
      if (author_title.trim()) {
        attribution += `, ${author_title.trim()}`
      }
      if (company.trim()) {
        attribution += `, ${company.trim()}`
      }
    }
    
    return attribution ? `"${quote.trim()}" — ${attribution}` : `"${quote.trim()}"`
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Quote</label>
        <textarea
          value={testimonial.quote}
          onChange={(e) => updateTestimonial("quote", e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
          rows={4}
          placeholder="Enter the client testimonial quote..."
        />
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Author Name</label>
          <input
            type="text"
            value={testimonial.author_name}
            onChange={(e) => updateTestimonial("author_name", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="John Smith"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            type="text"
            value={testimonial.author_title}
            onChange={(e) => updateTestimonial("author_title", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="CTO"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
          <input
            type="text"
            value={testimonial.company}
            onChange={(e) => updateTestimonial("company", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="TechCorp Inc"
          />
        </div>
      </div>
      
      {testimonial.quote && (
        <div className="mt-3 p-3 bg-gray-50 rounded-md">
          <label className="block text-xs font-medium text-gray-500 mb-1">Preview:</label>
          <div className="text-sm text-gray-700 italic">
            {formatPreview(testimonial)}
          </div>
        </div>
      )}
    </div>
  )
}

// Key Metrics Editor Component for title/description pairs
const KeyMetricsEditor = ({ value, onChange, maxItems = 3 }) => {
  // Parse initial value for key metrics structure
  const parseInitialValue = (val) => {
    if (!val) return [{ title: "", description: "" }]
    
    // Handle already structured array (new format)
    if (Array.isArray(val)) {
      if (val.length === 0) return [{ title: "", description: "" }]
      return val.map(item => ({
        title: item.title || "",
        description: item.description || ""
      }))
    }
    
    // Handle simple string array (legacy format) - convert to objects
    if (typeof val === "string") {
      // Parse markdown format with bullet points or title: description format
      const lines = val.split("\n").filter(line => line.trim())
      const metrics = []
      
      for (const line of lines) {
        const trimmed = line.trim().replace(/^[-•]\s*/, "")
        if (trimmed.includes(":")) {
          const [title, description] = trimmed.split(":", 2)
          metrics.push({
            title: title.trim(),
            description: description.trim()
          })
        } else if (trimmed) {
          // If no colon, treat as title only
          metrics.push({
            title: trimmed,
            description: ""
          })
        }
      }
      
      return metrics.length > 0 ? metrics : [{ title: "", description: "" }]
    }
    
    return [{ title: "", description: "" }]
  }

  // Use local state to prevent feedback loops
  const [localState, setLocalState] = React.useState(() => parseInitialValue(value))
  
  // Calculate validation state
  const canAdd = localState.length < maxItems
  const canRemove = localState.length > 0

  // Update local state when external value changes
  React.useEffect(() => {
    const newParsed = parseInitialValue(value)
    // Only update if significantly different to prevent loops
    if (JSON.stringify(newParsed) !== JSON.stringify(localState)) {
      setLocalState(newParsed)
    }
  }, [value])

  const handleMetricChange = (index, field, newValue) => {
    const updatedMetrics = [...localState]
    updatedMetrics[index] = { ...updatedMetrics[index], [field]: newValue }
    setLocalState(updatedMetrics)
    // Pass the structured array directly to match new backend expectation
    onChange(updatedMetrics)
  }

  const addMetric = () => {
    if (!canAdd) return
    const updatedMetrics = [...localState, { title: "", description: "" }]
    setLocalState(updatedMetrics)
    onChange(updatedMetrics)
  }

  const removeMetric = (index) => {
    let updatedMetrics = localState.filter((_, i) => i !== index)
    // Ensure at least one metric remains
    if (updatedMetrics.length === 0) {
      updatedMetrics = [{ title: "", description: "" }]
    }
    setLocalState(updatedMetrics)
    onChange(updatedMetrics)
  }

  return (
    <div className="space-y-3">
      {localState.map((metric, index) => (
        <div key={index} className="border border-gray-200 rounded-md p-3">
          <div className="flex justify-between items-start mb-2">
            <span className="text-sm font-medium text-gray-600">Metric {index + 1}</span>
            <button
              type="button"
              onClick={() => removeMetric(index)}
              disabled={!canRemove}
              className={`px-2 py-1 rounded text-sm ${
                canRemove 
                  ? "text-red-600 hover:bg-red-50" 
                  : "text-gray-400 cursor-not-allowed"
              }`}
            >
              Remove
            </button>
          </div>
          
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Title (flashy metric)</label>
              <input
                type="text"
                value={metric.title}
                onChange={(e) => handleMetricChange(index, "title", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., '40% Time Savings', 'Zero Security Incidents'"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={metric.description}
                onChange={(e) => handleMetricChange(index, "description", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                rows={2}
                placeholder="Brief explanation of what this metric means and how it was achieved..."
              />
            </div>
          </div>
        </div>
      ))}
      
      <button
        type="button"
        onClick={addMetric}
        disabled={!canAdd}
        className={`w-full px-3 py-2 text-sm border rounded-md ${
          canAdd
            ? "text-blue-600 border-blue-300 hover:bg-blue-50"
            : "text-gray-400 border-gray-300 cursor-not-allowed bg-gray-50"
        }`}
      >
        + Add Key Metric ({localState.length}/{maxItems})
      </button>
      {!canAdd && (
        <p className="text-red-500 text-xs">Maximum {maxItems} key metrics allowed</p>
      )}
    </div>
  )
}

// Outcomes Editor Component for title/description pairs (similar to KeyMetricsEditor but different context)
const OutcomesEditor = ({ value, onChange, maxItems = 3 }) => {
  // Parse initial value for outcomes structure
  const parseInitialValue = (val) => {
    if (!val) return [{ title: "", description: "" }]
    
    // Handle already structured array (new format)
    if (Array.isArray(val)) {
      if (val.length === 0) return [{ title: "", description: "" }]
      return val.map(item => ({
        title: item.title || "",
        description: item.description || ""
      }))
    }
    
    // Handle string format - could be markdown with headers and content
    if (typeof val === "string") {
      // Parse markdown format: ### Title\nDescription\n\n### Title2\nDescription2
      const sections = val.split(/(?=### )/).filter(section => section.trim())
      const outcomes = []
      
      for (const section of sections) {
        const trimmed = section.trim()
        if (trimmed.startsWith("### ")) {
          const lines = trimmed.split("\n")
          const title = lines[0].replace("### ", "").trim()
          const description = lines.slice(1).join("\n").trim()
          if (title) {
            outcomes.push({ title, description })
          }
        } else if (trimmed.includes(":")) {
          // Handle title: description format
          const [title, description] = trimmed.split(":", 2)
          outcomes.push({
            title: title.trim(),
            description: description.trim()
          })
        }
      }
      
      return outcomes.length > 0 ? outcomes : [{ title: "", description: "" }]
    }
    
    return [{ title: "", description: "" }]
  }

  // Use local state to prevent feedback loops
  const [localState, setLocalState] = React.useState(() => parseInitialValue(value))
  
  // Calculate validation state
  const canAdd = localState.length < maxItems
  const canRemove = localState.length > 0

  // Update local state when external value changes
  React.useEffect(() => {
    const newParsed = parseInitialValue(value)
    // Only update if significantly different to prevent loops
    if (JSON.stringify(newParsed) !== JSON.stringify(localState)) {
      setLocalState(newParsed)
    }
  }, [value])

  const handleOutcomeChange = (index, field, newValue) => {
    const updatedOutcomes = [...localState]
    updatedOutcomes[index] = { ...updatedOutcomes[index], [field]: newValue }
    setLocalState(updatedOutcomes)
    // Pass the structured array directly to match new backend expectation
    onChange(updatedOutcomes)
  }

  const addOutcome = () => {
    if (!canAdd) return
    const updatedOutcomes = [...localState, { title: "", description: "" }]
    setLocalState(updatedOutcomes)
    onChange(updatedOutcomes)
  }

  const removeOutcome = (index) => {
    let updatedOutcomes = localState.filter((_, i) => i !== index)
    // Ensure at least one outcome remains
    if (updatedOutcomes.length === 0) {
      updatedOutcomes = [{ title: "", description: "" }]
    }
    setLocalState(updatedOutcomes)
    onChange(updatedOutcomes)
  }

  return (
    <div className="space-y-3">
      {localState.map((outcome, index) => (
        <div key={index} className="border border-gray-200 rounded-md p-3">
          <div className="flex justify-between items-start mb-2">
            <span className="text-sm font-medium text-gray-600">Outcome {index + 1}</span>
            <button
              type="button"
              onClick={() => removeOutcome(index)}
              disabled={!canRemove}
              className={`px-2 py-1 rounded text-sm ${
                canRemove 
                  ? "text-red-600 hover:bg-red-50" 
                  : "text-gray-400 cursor-not-allowed"
              }`}
            >
              Remove
            </button>
          </div>
          
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={outcome.title}
                onChange={(e) => handleOutcomeChange(index, "title", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 'Streamlined Compliance Process', 'Enhanced Security Posture'"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={outcome.description}
                onChange={(e) => handleOutcomeChange(index, "description", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                rows={3}
                placeholder="Detailed explanation of the outcome, including specific benefits and how it addresses the original challenges..."
              />
            </div>
          </div>
        </div>
      ))}
      
      <button
        type="button"
        onClick={addOutcome}
        disabled={!canAdd}
        className={`w-full px-3 py-2 text-sm border rounded-md ${
          canAdd
            ? "text-blue-600 border-blue-300 hover:bg-blue-50"
            : "text-gray-400 border-gray-300 cursor-not-allowed bg-gray-50"
        }`}
      >
        + Add Outcome ({localState.length}/{maxItems})
      </button>
      {!canAdd && (
        <p className="text-red-500 text-xs">Maximum {maxItems} outcomes allowed</p>
      )}
    </div>
  )
}

// Call to Action Editor Component for title/description pair
const CallToActionEditor = ({ value, onChange }) => {
  // Parse initial value for call to action structure
  const parseInitialValue = (val) => {
    if (!val) return { title: "", description: "" }
    
    // Handle already structured object (new format)
    if (typeof val === "object" && val !== null && !Array.isArray(val)) {
      return {
        title: val.title || "",
        description: val.description || ""
      }
    }
    
    // Handle string format - try to parse markdown header format
    if (typeof val === "string") {
      // Look for ### Title pattern followed by description
      const headerMatch = val.match(/^### (.+?)\n([\s\S]*)$/)
      if (headerMatch) {
        return {
          title: headerMatch[1].trim(),
          description: headerMatch[2].trim()
        }
      }
      
      // Look for ## Title pattern
      const h2Match = val.match(/^## (.+?)\n([\s\S]*)$/)
      if (h2Match) {
        return {
          title: h2Match[1].trim(),
          description: h2Match[2].trim()
        }
      }
      
      // If no header pattern, treat as description only with default title
      return {
        title: "Ready to Transform Your Security Posture?",
        description: val.trim()
      }
    }
    
    return { title: "", description: "" }
  }

  // Use local state to prevent feedback loops
  const [localState, setLocalState] = React.useState(() => parseInitialValue(value))

  // Update local state when external value changes
  React.useEffect(() => {
    const newParsed = parseInitialValue(value)
    // Only update if significantly different to prevent loops
    if (JSON.stringify(newParsed) !== JSON.stringify(localState)) {
      setLocalState(newParsed)
    }
  }, [value])

  const handleFieldChange = (field, newValue) => {
    const updated = { ...localState, [field]: newValue }
    setLocalState(updated)
    // Pass the structured object directly to match new backend expectation
    onChange(updated)
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">CTA Title</label>
        <input
          type="text"
          value={localState.title}
          onChange={(e) => handleFieldChange("title", e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., 'Ready to Transform Your Compliance Journey?'"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">CTA Description</label>
        <textarea
          value={localState.description}
          onChange={(e) => handleFieldChange("description", e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
          rows={4}
          placeholder="2-3 sentences encouraging the reader to contact RISCPoint, highlighting value proposition and next steps..."
        />
      </div>
      
      {(localState.title || localState.description) && (
        <div className="mt-3 p-3 bg-blue-50 rounded-md border-l-4 border-blue-500">
          <label className="block text-xs font-medium text-blue-700 mb-1">Preview:</label>
          <div className="text-sm text-blue-900">
            {localState.title && <div className="font-semibold mb-1">{localState.title}</div>}
            {localState.description && <div>{localState.description}</div>}
          </div>
        </div>
      )}
    </div>
  )
}

// Partnership Details Editor Component for complex nested structure
const PartnershipDetailsEditor = ({ value, onChange, minItems = 2, maxItems = 4 }) => {
  // Parse initial value for partnership details structure
  const parseInitialValue = (val) => {
    if (!val) {
      return {
        client_overview: "",
        partnership_details: [{ title: "", description: "" }]
      }
    }
    
    // Handle already structured object
    if (typeof val === "object" && val !== null && !Array.isArray(val)) {
      return {
        client_overview: val.client_overview || "",
        partnership_details: Array.isArray(val.partnership_details) 
          ? val.partnership_details 
          : [{ title: "", description: "" }]
      }
    }
    
    // Handle string input - try to parse markdown format
    if (typeof val === "string") {
      const sections = val.split(/(?=### )/).filter(section => section.trim())
      const partnershipItems = []
      let clientOverview = ""
      
      for (const section of sections) {
        const trimmed = section.trim()
        if (trimmed.startsWith("### ")) {
          const lines = trimmed.split("\n")
          const title = lines[0].replace("### ", "").trim()
          const description = lines.slice(1).join("\n").trim()
          if (title && description) {
            partnershipItems.push({ title, description })
          }
        } else if (trimmed && !clientOverview) {
          // First non-header content becomes client overview
          clientOverview = trimmed
        }
      }
      
      return {
        client_overview: clientOverview,
        partnership_details: partnershipItems.length > 0 ? partnershipItems : [{ title: "", description: "" }]
      }
    }
    
    return {
      client_overview: "",
      partnership_details: [{ title: "", description: "" }]
    }
  }

  // Use local state to prevent feedback loops
  const [localState, setLocalState] = React.useState(() => parseInitialValue(value))
  
  // Calculate validation state
  const canAdd = localState.partnership_details.length < maxItems
  const canRemove = localState.partnership_details.length > minItems

  // Update local state when external value changes
  React.useEffect(() => {
    const newParsed = parseInitialValue(value)
    // Only update if significantly different to prevent loops
    if (JSON.stringify(newParsed) !== JSON.stringify(localState)) {
      setLocalState(newParsed)
    }
  }, [value])

  const handleClientOverviewChange = (newOverview) => {
    const updated = { ...localState, client_overview: newOverview }
    setLocalState(updated)
    onChange(updated)
  }

  const handlePartnershipItemChange = (index, field, newValue) => {
    const updatedItems = [...localState.partnership_details]
    updatedItems[index] = { ...updatedItems[index], [field]: newValue }
    const updated = { ...localState, partnership_details: updatedItems }
    setLocalState(updated)
    onChange(updated)
  }

  const addPartnershipItem = () => {
    if (!canAdd) return
    const updatedItems = [...localState.partnership_details, { title: "", description: "" }]
    const updated = { ...localState, partnership_details: updatedItems }
    setLocalState(updated)
    onChange(updated)
  }

  const removePartnershipItem = (index) => {
    if (!canRemove) return
    let updatedItems = localState.partnership_details.filter((_, i) => i !== index)
    // Ensure at least minimum items remain
    if (updatedItems.length < minItems) {
      return // Don't allow removal below minimum
    }
    const updated = { ...localState, partnership_details: updatedItems }
    setLocalState(updated)
    onChange(updated)
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Client Overview
        </label>
        <textarea
          value={localState.client_overview}
          onChange={(e) => handleClientOverviewChange(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
          rows={3}
          placeholder="Enter a positive overview of the client, highlighting their industry standing and market position..."
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Partnership Details
        </label>
        <div className="space-y-3">
          {localState.partnership_details.map((item, index) => (
            <div key={index} className="border border-gray-200 rounded-md p-3">
              <div className="flex justify-between items-start mb-2">
                <span className="text-sm font-medium text-gray-600">Detail {index + 1}</span>
                <button
                  type="button"
                  onClick={() => removePartnershipItem(index)}
                  disabled={!canRemove}
                  className={`px-2 py-1 rounded text-sm ${
                    canRemove 
                      ? "text-red-600 hover:bg-red-50" 
                      : "text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Remove
                </button>
              </div>
              
              <div className="space-y-2">
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => handlePartnershipItemChange(index, "title", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Partnership aspect title (e.g., 'Strategic Planning', 'Implementation Support')"
                />
                <textarea
                  value={item.description}
                  onChange={(e) => handlePartnershipItemChange(index, "description", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                  rows={2}
                  placeholder="Detailed explanation of how RISCPoint helped achieve outcomes..."
                />
              </div>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addPartnershipItem}
            disabled={!canAdd}
            className={`w-full px-3 py-2 text-sm border rounded-md ${
              canAdd
                ? "text-blue-600 border-blue-300 hover:bg-blue-50"
                : "text-gray-400 border-gray-300 cursor-not-allowed bg-gray-50"
            }`}
          >
            + Add Partnership Detail ({localState.partnership_details.length}/{maxItems})
          </button>
          {!canAdd && (
            <p className="text-red-500 text-xs">Maximum {maxItems} partnership details allowed</p>
          )}
          {!canRemove && (
            <p className="text-red-500 text-xs">Minimum {minItems} partnership details required</p>
          )}
        </div>
      </div>
    </div>
  )
}

// Unified Section Editor Component - Simplified for single edit mode
const UnifiedSectionEditor = ({
  section,
  mode = "edit",
  currentContent = "",
  customContent = {},
  onCustomContentChange,
}) => {
  const isListField = ["objectives", "challenges"].includes(section.key)
  const isOutcomesField = section.key === "outcomes"
  const isTestimonialField = section.key === "testimonial"
  const isKeyMetricsField = section.key === "key_metrics"
  const isPartnershipDetailsField = section.key === "partnership_details"
  const isCallToActionField = section.key === "call_to_action"

  // Get the content to show as the starting point for custom editing
  const getInitialCustomContent = () => {
    // If user has already provided custom content, use that
    if (customContent[section.key] !== undefined && customContent[section.key] !== "") {
      return customContent[section.key]
    }

    // Otherwise, pre-fill with current content to make editing easier
    return currentContent || ""
  }

  const handleCustomContentChange = (content) => {
    if (onCustomContentChange) {
      onCustomContentChange(section.key, content)
    }
  }

  return (
    <div className="space-y-3">
      {isTestimonialField ? (
        <div>
          <ClientTestimonialEditor value={getInitialCustomContent()} onChange={handleCustomContentChange} />
          <p className="text-xs text-gray-500 mt-2">💡 Content is pre-filled with current version for easy editing</p>
        </div>
      ) : isKeyMetricsField ? (
        <div>
          <KeyMetricsEditor value={getInitialCustomContent()} onChange={handleCustomContentChange} maxItems={3} />
          <p className="text-xs text-gray-500 mt-2">💡 Content is pre-filled with current version for easy editing</p>
        </div>
      ) : isOutcomesField ? (
        <div>
          <OutcomesEditor value={getInitialCustomContent()} onChange={handleCustomContentChange} maxItems={3} />
          <p className="text-xs text-gray-500 mt-2">💡 Content is pre-filled with current version for easy editing</p>
        </div>
      ) : isPartnershipDetailsField ? (
        <div>
          <PartnershipDetailsEditor value={getInitialCustomContent()} onChange={handleCustomContentChange} minItems={2} maxItems={4} />
          <p className="text-xs text-gray-500 mt-2">💡 Content is pre-filled with current version for easy editing</p>
        </div>
      ) : isCallToActionField ? (
        <div>
          <CallToActionEditor value={getInitialCustomContent()} onChange={handleCustomContentChange} />
          <p className="text-xs text-gray-500 mt-2">💡 Content is pre-filled with current version for easy editing</p>
        </div>
      ) : isListField ? (
        <div>
          <SimpleListEditor value={getInitialCustomContent()} onChange={handleCustomContentChange} fieldName={section.key} />
          <p className="text-xs text-gray-500 mt-2">💡 Content is pre-filled with current version for easy editing</p>
        </div>
      ) : (
        <div>
          <MarkdownBoldTextarea
            value={getInitialCustomContent()}
            onChange={handleCustomContentChange}
            placeholder={`Enter custom content for ${section.label.toLowerCase()}...`}
          />
          <p className="text-xs text-gray-500 mt-2">💡 Content is pre-filled with current version for easy editing</p>
        </div>
      )}
    </div>
  )
}

export default UnifiedSectionEditor
